import type { I18n } from './types'
import de from './de'

export interface LanguageInfo {
    /** the language's name in itself (Deutsch, Italiano), shown in the switcher */
    name: string
    /** locale for numbers (and Intl in general) */
    locale: string
    /** locale for dates written by Intl / toLocaleDateString */
    dateLocale: string
    /** loads the language's texts; only the chosen language is downloaded */
    load: () => Promise<I18n>
}

/**
 * The languages plato.HR speaks. German is the source and is always bundled;
 * every other language is a separate download, loaded when someone picks it.
 *
 * Adding one: copy src/i18l/de to src/i18l/<lang>, translate the JSON files,
 * run `npm run i18n:types`, then add a line here, e.g.
 *   it: { name: 'Italiano', locale: 'it-IT', dateLocale: 'it-IT', load: () => import('./it').then((m) => m.default) },
 *
 * English writes dates the European way, day first with dots (04.11.2026): Swiss English (en-CH)
 * is the English locale that does, so it is the date locale; numbers stay British (en-CH would
 * group thousands as 1'234.50).
 */
export const LANGUAGES = {
    de: { name: 'Deutsch', locale: 'de-DE', dateLocale: 'de-DE', load: async () => de },
    it: { name: 'Italiano', locale: 'it-IT', dateLocale: 'it-IT', load: () => import('./it').then((m) => m.default) },
    en: { name: 'English', locale: 'en-GB', dateLocale: 'en-CH', load: () => import('./en').then((m) => m.default) },
} satisfies Record<string, LanguageInfo>

export type Lang = keyof typeof LANGUAGES

export const SOURCE_LANG: Lang = 'de'
export const SOURCE_TEXTS: I18n = de

export function isLang(value: unknown): value is Lang {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LANGUAGES, value)
}

export function languageList(): { lang: Lang; name: string }[] {
    return (Object.keys(LANGUAGES) as Lang[]).map((lang) => ({ lang, name: LANGUAGES[lang].name }))
}

const STORAGE_KEY = 'plato.hr.lang'

/** the language this person picked before, if any (kept per browser) */
export function readStoredLang(): Lang | null {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY)
        return isLang(value) ? value : null
    } catch {
        return null
    }
}

export function storeLang(lang: Lang): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, lang)
    } catch {
        // private window or blocked storage: the choice lasts until reload
    }
}

/** first browser language plato.HR speaks (it-IT -> it), otherwise German */
export function browserLang(): Lang {
    try {
        const wanted = navigator.languages?.length ? navigator.languages : [navigator.language]
        for (const tag of wanted) {
            const base = (tag ?? '').toLowerCase().split('-')[0]
            if (isLang(base)) return base
        }
    } catch {
        // no navigator (tests)
    }
    return SOURCE_LANG
}

/** the language to start with: the stored choice, else the browser's */
export function initialLang(): Lang {
    return readStoredLang() ?? browserLang()
}
